import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Table,
  Button,
  IconButton,
  TextField,
  Modal,
  Skeleton,
  CircularProgress,
  Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { headingStyle } from "../../style/LapPageStyle";
import { DShapeSaveButton } from "../../style/AppointmentStyle";
import { useSelector } from "react-redux";
import { getMedicalReports } from "../../connections/getApi";
import {
  MedicalTestReportsPrescription,
  MedicalTestSummary,
} from "../../connections/postApi";
import { showToast } from "../../utils/toastUtil";
import { textfieldTextPrep } from "../../style/PatientDetailsStyle";
import ContentPasteSearchIcon from "@mui/icons-material/ContentPasteSearch";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import not_found from "../../assets/not_found.svg";
import DoneIcon from "@mui/icons-material/Done";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ScreenShareOutlinedIcon from "@mui/icons-material/ScreenShareOutlined";

const tableRowSx = {
  backgroundColor: "neutral.blueShadeBg",
  paddingY: "6px",
  cursor: "pointer",
  borderBottom: "8px solid white",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    transform: "scale(1.001)",
    borderRadius: "20px !important",
  },
};

const COLLAPSED_HEIGHT = 82;

const SkeletonRows = () =>
  [...Array(6)].map((_, i) => (
    <TableRow key={i} sx={{ borderBottom: "8px solid white" }}>
      <TableCell
        sx={{
          width: "20%",
          padding: "10px 15px",
          borderTopLeftRadius: "20px",
          borderBottomLeftRadius: "20px",
          backgroundColor: "neutral.blueShadeBg",
        }}
      >
        <Skeleton variant="text" width="70%" height={28} />
      </TableCell>
      <TableCell
        sx={{
          width: "30%",
          padding: "10px 15px",
          backgroundColor: "neutral.blueShadeBg",
        }}
      >
        <Skeleton variant="rounded" width={220} height={38} />
        <Skeleton variant="text" width="50%" height={18} sx={{ mt: "4px" }} />
      </TableCell>
      <TableCell
        sx={{
          display: "flex",
          gap: "50px",
          padding: "10px 15px",
          borderTopRightRadius: "20px",
          borderBottomRightRadius: "20px",
          backgroundColor: "neutral.blueShadeBg",
        }}
      >
        {[80, 80, 80].map((w, j) => (
          <Box key={j}>
            <Skeleton variant="text" width={w} height={22} />
            <Skeleton variant="text" width={w} height={16} />
          </Box>
        ))}
      </TableCell>
    </TableRow>
  ));

/* Renders one ai_summary value — string or string[] */
const SummaryValue = ({ value }) => {
  if (Array.isArray(value)) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4, mt: 0.5 }}>
        {value.map((item, idx) => (
          <Box
            key={idx}
            sx={{ display: "flex", alignItems: "flex-start", gap: 0.8 }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: "neutral.teal",
                mt: "7px",
                flexShrink: 0,
              }}
            />
            <Typography fontSize="13px" lineHeight={1.8} color="text.primary">
              {item}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  return (
    <Typography
      fontSize="13px"
      lineHeight={1.8}
      color="text.primary"
      sx={{ mt: 0.5 }}
    >
      {value}
    </Typography>
  );
};

const AiSummaryCard = ({ data, onClose }) => {
  const contentRef = useRef(null);
  const [overflows, setOverflows] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (contentRef.current) {
      setOverflows(contentRef.current.scrollHeight > COLLAPSED_HEIGHT);
    }
  }, [data]);

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "neutral.teal",
        borderRadius: 3,
        backgroundColor: "#fff",
        mb: 2,
        overflow: "hidden",
      }}
    >
      {/* Card header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          backgroundColor: "neutral.teal",
          borderRadius: "12px 12px 0 0",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeIcon sx={{ color: "#fff", fontSize: 16 }} />
          <Typography fontWeight={700} fontSize="13px" color="#fff">
            AI Summary
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: "#fff", p: 0.3 }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Collapsible content */}
      <Box
        ref={contentRef}
        sx={{
          px: 2,
          pt: 1.5,
          pb: overflows && !expanded ? 0 : 1.5,
          maxHeight: expanded ? "none" : COLLAPSED_HEIGHT,
          overflow: "hidden",
          transition: "max-height 0.3s ease",
        }}
      >
        {Object.entries(data).map(([key, value]) => (
          <Box key={key} sx={{ mb: 1.2 }}>
            <Typography
              fontSize="12px"
              fontWeight={700}
              sx={{
                textTransform: "capitalize",
                color: "neutral.teal",
                letterSpacing: "0.3px",
              }}
            >
              {key.replace(/_/g, " ")}
            </Typography>
            <SummaryValue value={value} />
          </Box>
        ))}
      </Box>

      {/* View more / less */}
      {overflows && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            borderTop: expanded ? "1px solid #e0f2f1" : "none",
            background: expanded
              ? "transparent"
              : "linear-gradient(to bottom, transparent, #fff 70%)",
            mt: expanded ? 0 : "-28px",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Button
            size="small"
            onClick={() => setExpanded((p) => !p)}
            endIcon={
              expanded ? (
                <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
              ) : (
                <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
              )
            }
            sx={{
              textTransform: "none",
              fontSize: "12px",
              color: "neutral.teal",
              fontWeight: 600,
              py: 0.5,
            }}
          >
            {expanded ? "View Less" : "View More"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

const PatientMedicalReports = ({ onClose }) => {
  const patientDetailsData = useSelector(
    (state) => state.prescription.patientDetailsData,
  );

  const [testReportsData, setTestReports] = useState({});
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);

  const handleValueChange = (e, reportIndex) => {
    const { value } = e.target;
    setTestReports((prevReports) => {
      const updatedReports = { ...prevReports };
      updatedReports.reports_medical_test = [
        ...prevReports.reports_medical_test,
      ];
      updatedReports.reports_medical_test[reportIndex] = {
        ...prevReports.reports_medical_test[reportIndex],
        reports: [
          {
            ...prevReports.reports_medical_test[reportIndex]?.reports[0],
            medical_report_value: value,
          },
        ],
      };
      return updatedReports;
    });
  };

  const saveReport = async (report) => {
    const data = {
      id: report?.reports[0]?.report_id,
      medical_test_report: { value: report?.reports[0]?.medical_report_value },
    };
    try {
      const response = await MedicalTestReportsPrescription(data);
      showToast(
        response?.status?.message || response?.status?.status,
        response?.status?.status,
      );
    } catch (error) {
      console.error("Error saving report:", error);
    }
  };

  useEffect(() => {
    const fetchMedicalReports = async () => {
      setLoading(true);
      try {
        const res = await getMedicalReports(patientDetailsData.id);
        setTestReports(res);
      } catch (error) {
        console.error("Error fetching medical reports:", error);
      } finally {
        setLoading(false);
      }
    };
    if (patientDetailsData?.id) fetchMedicalReports();
  }, [patientDetailsData]);

  const fetchSummary = async () => {
    setSummaryLoading(true);
    setAiSummary(null);
    try {
      const res = await MedicalTestSummary(
        patientDetailsData.id,
        "test_reports",
      );
      setAiSummary(res?.payload?.ai_summary || null);
    } catch (error) {
      console.error("Error fetching summary:", error);
    } finally {
      setSummaryLoading(false);
    }
  };

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);

  const openViewer = (file) => {
    if (!file?.url) return;
    setViewerFile(file);
    setViewerOpen(true);
  };

  const closeViewer = () => {
    setViewerOpen(false);
    setViewerFile(null);
  };

  return (
    <Box sx={{ width: "90vw", overflowX: "auto" }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box sx={{ width: 40 }} />
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ContentPasteSearchIcon
            sx={{
              color: "neutral.teal",
              width: "28px",
              height: "28px",
              mr: 1,
              mt: "-4px",
            }}
          />
          <Typography
            variant="heading1"
            fontWeight={600}
            gutterBottom
            sx={{ ...headingStyle }}
          >
            Test Reports
          </Typography>
        </Box>
        <IconButton sx={{ marginBottom: "2rem" }} onClick={onClose}>
          <CloseIcon size="small" sx={{ color: "red" }} />
        </IconButton>
      </Box>

      {/* Patient details + AI button */}
      <Grid
        container
        sx={{
          justifyContent: "space-between",
          width: "100%",
          mt: "30px",
          mb: "16px",
          alignItems: "center",
        }}
      >
        <Typography>Name: {testReportsData?.full_name}</Typography>
        <Typography>Patient ID: {testReportsData?.uid}</Typography>
        <Typography>Age: {testReportsData?.current_age}</Typography>
        <Typography>Gender: {patientDetailsData?.gender || "-"}</Typography>

        {/* AI Summary trigger */}
        <Button
          size="small"
          onClick={fetchSummary}
          disabled={summaryLoading}
          variant="outlined"
          sx={{
            borderRadius: "20px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "12px",
            px: 1.5,
            borderColor: "neutral.teal",
            color: "neutral.teal",
            minWidth: 0,
            "&:hover": { backgroundColor: "rgba(0,128,128,0.06)" },
          }}
        >
          {summaryLoading ? (
            <CircularProgress
              size={14}
              sx={{ color: "neutral.teal", mr: 0.5 }}
            />
          ) : (
            <AutoAwesomeIcon sx={{ fontSize: 15, mr: 0.5 }} />
          )}
          {summaryLoading ? "Generating..." : "AI Summary"}
        </Button>
      </Grid>

      {/* AI Summary card */}
      {aiSummary && (
        <AiSummaryCard data={aiSummary} onClose={() => setAiSummary(null)} />
      )}

      {/* Reports table */}
      <Grid container spacing={2} marginBottom="auto">
        <Grid item xs={12}>
          <TableContainer
            sx={{
              mt: 1,
              width: "100%",
              "&::-webkit-scrollbar": { display: "none" },
              "-ms-overflow-style": "none",
              "scrollbar-width": "none",
            }}
          >
            {loading ? (
              <Table sx={{ width: "100%" }}>
                <TableBody>
                  <SkeletonRows />
                </TableBody>
              </Table>
            ) : testReportsData?.reports_medical_test?.length > 0 ||
              testReportsData?.attachments?.length > 0 ? (
              <Table
                sx={{
                  width: "100%",
                  "&::-webkit-scrollbar": { display: "none" },
                  "-ms-overflow-style": "none",
                  "scrollbar-width": "none",
                }}
              >
                <TableBody>
                  {testReportsData?.reports_medical_test?.map(
                    (report, index) => (
                      <TableRow
                        sx={{
                          ...tableRowSx,
                          borderTop: index === 0 ? "2px solid white" : "",
                        }}
                        key={index}
                      >
                        <TableCell
                          sx={{
                            padding: "2px",
                            paddingLeft: "15px",
                            width: "20%",
                            marginTop: "10px",
                            borderTopLeftRadius: "20px",
                            borderBottomLeftRadius: "20px",
                          }}
                        >
                          <Typography
                            sx={{ padding: "0px" }}
                            variant="heading2"
                            fontWeight="600"
                          >
                            {report?.medical_test}
                          </Typography>
                        </TableCell>

                        <TableCell
                          sx={{
                            padding: "0px",
                            paddingLeft: "15px",
                            width: "30%",
                          }}
                        >
                          <Box
                            sx={{
                              width: "220px",
                              display: "flex",
                              justifyContent: "space-between",
                              mt: 1,
                            }}
                          >
                            <Box>
                              <TextField
                                placeholder="Enter value"
                                sx={{ ...textfieldTextPrep }}
                                variant="outlined"
                                name="valueAction"
                                value={
                                  report?.reports[0]?.medical_report_value || ""
                                }
                                onChange={(e) => handleValueChange(e, index)}
                              />
                              <Typography
                                variant="body2"
                                color="textSecondary"
                                sx={{ mt: "2px", ml: "6px" }}
                              >
                                {report?.reports[0]?.generated_at}
                              </Typography>
                            </Box>
                            <Button
                              sx={{
                                ...DShapeSaveButton,
                                height: "38px",
                                color: "white",
                              }}
                              onClick={() => saveReport(report)}
                            >
                              <DoneIcon sx={{ color: "neutral.navyBlue" }} />
                            </Button>
                          </Box>
                        </TableCell>

                        <TableCell
                          sx={{
                            display: "flex",
                            overflowX: "auto",
                            borderTopRightRadius: "20px",
                            gap: "70px",
                            borderBottomRightRadius: "20px",
                          }}
                        >
                          {report?.reports?.map((el, i) => (
                            <Box key={i}>
                              {el?.medical_report_value || "-"}
                              <br />
                              {el?.generated_at}
                            </Box>
                          ))}
                        </TableCell>
                      </TableRow>
                    ),
                  )}
                </TableBody>
              </Table>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  width: "30%",
                  margin: "auto",
                }}
              >
                <Box sx={{ margin: "auto", width: "40%" }}>
                  <img src={not_found} alt="" width="80%" />
                </Box>
                <Typography align="center" fontSize="17px" color="crimson">
                  Test reports not found for selected patient
                </Typography>
              </Box>
            )}
          </TableContainer>

          {testReportsData?.attachments?.length > 0 && (
            <Box mt={2} sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {testReportsData.attachments.map((file, groupIdx) => {
                const isPDF = file.content_type === "application/pdf";
                const isImage = file.content_type?.startsWith("image/");
                return (
                  <Box
                    key={groupIdx}
                    sx={{
                      width: 300,
                      height: 140,
                      borderRadius: "14px",
                      backgroundColor: "neutral.blueShadeBg",
                      position: "relative",
                      border: "1px solid #E1E1E1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 2,
                    }}
                  >
                    <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                      <IconButton size="small" onClick={() => openViewer(file)}>
                        <VisibilityOutlinedIcon
                          sx={{ fontSize: 18, color: "neutral.teal" }}
                        />
                      </IconButton>
                    </Box>
                    <Box
                      sx={{
                        textAlign: "center",
                        flexDirection: "column",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isPDF ? (
                        <PictureAsPdfOutlinedIcon
                          sx={{ fontSize: 30, color: "neutral.teal" }}
                        />
                      ) : isImage ? (
                        <ImageOutlinedIcon
                          sx={{ fontSize: 30, color: "neutral.teal" }}
                        />
                      ) : (
                        <DescriptionOutlinedIcon
                          sx={{ fontSize: 30, color: "neutral.teal" }}
                        />
                      )}
                      <Typography
                        variant="subTitle116px"
                        sx={{
                          mt: 1,
                          maxWidth: "220px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontFamily: "Lato",
                          color: "neutral.black",
                        }}
                      >
                        {file?.filename}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Grid>
      </Grid>

      {/* File viewer modal */}
      <Modal
        open={viewerOpen}
        onClose={closeViewer}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          sx={{
            width: "90vw",
            height: "90vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: 24,
            position: "relative",
          }}
        >
          <Box
            sx={{
              height: 54,
              px: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: "1px solid #E5E7EB",
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>
              {viewerFile?.filename || "Preview"}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Button
                size="small"
                onClick={() =>
                  viewerFile?.url && window.open(viewerFile.url, "_blank")
                }
              >
                <ScreenShareOutlinedIcon sx={{ color: "neutral.teal" }} />
              </Button>
              <IconButton onClick={closeViewer}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ width: "100%", height: "calc(90vh - 54px)" }}>
            {viewerFile?.content_type === "application/pdf" ? (
              <iframe
                title="pdf-viewer"
                src={viewerFile?.url}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            ) : viewerFile?.content_type?.startsWith("image/") ? (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "#000",
                }}
              >
                <img
                  src={viewerFile?.url}
                  alt={viewerFile?.filename || "image"}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography>
                  Preview not supported for this file type.
                </Typography>
                <Button
                  sx={{ mt: 2 }}
                  variant="contained"
                  onClick={() =>
                    viewerFile?.url && window.open(viewerFile.url, "_blank")
                  }
                >
                  Download / Open
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default PatientMedicalReports;
